import type { InvoiceListQuery, OrderListQuery } from '@faktorplus/shared';

/**
 * Central registry of React Query keys.
 *
 * Having them in one file makes invalidation after a workflow action a matter
 * of naming a key, not of remembering every screen that shows the invoice.
 */
export const queryKeys = {
  invoices: {
    all: ['invoices'] as const,
    list: (query: Partial<InvoiceListQuery>) => ['invoices', 'list', query] as const,
    detail: (id: string) => ['invoices', 'detail', id] as const,
    suggestion: (vendorId: string) => ['invoices', 'suggestion', vendorId] as const,
    unsettledCorrections: ['invoices', 'corrections', 'unsettled'] as const,
  },
  projects: {
    all: ['projects'] as const,
    detail: (id: string) => ['projects', 'detail', id] as const,
    dashboard: (id: string, period: Record<string, string | undefined>) =>
      ['projects', 'dashboard', id, period] as const,
  },
  orders: {
    all: ['orders'] as const,
    list: (query: Partial<OrderListQuery>) => ['orders', 'list', query] as const,
  },
  vendors: {
    all: ['vendors'] as const,
  },
  contacts: {
    all: ['contacts'] as const,
  },
  documents: {
    all: ['documents'] as const,
    categories: (includeInactive: boolean) =>
      ['documents', 'categories', { includeInactive }] as const,
    forProject: (projectId: string, filters: Record<string, string | undefined>) =>
      ['documents', 'project', projectId, filters] as const,
    templates: ['documents', 'templates'] as const,
  },
  company: {
    all: ['company'] as const,
  },
  categories: {
    all: ['cost-categories'] as const,
  },
  employees: {
    all: ['employees'] as const,
  },
  users: {
    all: ['users'] as const,
    byRole: (role: string) => ['users', role] as const,
  },
  reports: {
    dashboard: ['reports', 'dashboard'] as const,
    activity: ['reports', 'activity'] as const,
    costsByProject: (query: Record<string, string>) =>
      ['reports', 'costs-by-project', query] as const,
  },
} as const;
